<template>
  <div class="dj-region-cascader" :class="{ open: open, disabled: disabled }">
    <button
      type="button"
      class="dj-region-trigger"
      :class="{ placeholder: !displayText }"
      :disabled="disabled"
      @click.stop="toggle">
      <span>{{ displayText || placeholder }}</span>
      <i></i>
    </button>
    <div v-show="open" class="dj-region-panel" @click.stop>
      <div class="dj-region-col">
        <div v-if="loading" class="dj-region-empty">区域加载中…</div>
        <div v-else-if="!provinces.length" class="dj-region-empty">暂无区域，请先在「区域」菜单获取</div>
        <button
          v-for="item in provinces"
          :key="item.code"
          type="button"
          class="dj-region-item"
          :class="{ active: item.code === provinceCode }"
          @click="pickProvince(item)">
          <span>{{ item.name }}</span><b>{{ isLeaf(item) ? '✓' : '✓ ›' }}</b>
        </button>
      </div>
      <div class="dj-region-col" :class="{ muted: !cities.length }">
        <div v-if="!cities.length" class="dj-region-empty">{{ provinceCode ? '暂无下级（可只选到省）' : '请先选择省份' }}</div>
        <button
          v-for="item in cities"
          :key="item.code"
          type="button"
          class="dj-region-item"
          :class="{ active: item.code === cityCode }"
          @click="pickCity(item)">
          <span>{{ item.name }}</span><b>{{ isLeaf(item) ? '✓' : '✓ ›' }}</b>
        </button>
      </div>
      <div class="dj-region-col" :class="{ muted: !districts.length }">
        <div v-if="!districts.length" class="dj-region-empty">{{ cityCode ? '暂无下级（可只选到市）' : '可选下级市/区，或只选到当前级' }}</div>
        <button
          v-for="item in districts"
          :key="item.code"
          type="button"
          class="dj-region-item"
          :class="{ active: item.code === districtCode }"
          @click="pickDistrict(item)">
          <span>{{ item.name }}</span><b>✓</b>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { chinaArea, findRegionPath, loadChinaArea } from './regionChina'

export default {
  name: 'RegionCascader',
  props: {
    value: { type: [String, Number], default: undefined },
    disabled: { type: Boolean, default: false },
    placeholder: { type: String, default: '可任选省 / 市 / 区县' },
    fallbackText: { type: String, default: '' }
  },
  data () {
    return {
      open: false,
      loading: false,
      tree: chinaArea,
      provinceCode: undefined,
      cityCode: undefined,
      districtCode: undefined
    }
  },
  computed: {
    provinces () { return this.tree },
    provinceNode () { return this.provinces.find(item => item.code === this.provinceCode) },
    cities () { return (this.provinceNode && this.provinceNode.children) || [] },
    cityNode () { return this.cities.find(item => item.code === this.cityCode) },
    districts () { return (this.cityNode && this.cityNode.children) || [] },
    displayText () {
      const names = [this.provinceNode, this.cityNode, this.districts.find(item => item.code === this.districtCode)]
        .filter(Boolean)
        .map(item => item.name)
      if (names.length) return names.join(' / ')
      return this.fallbackText || ''
    }
  },
  watch: {
    value: {
      immediate: true,
      handler (value) { this.applyValue(value) }
    }
  },
  created () {
    this.ensureTree()
  },
  mounted () {
    document.addEventListener('click', this.onDocumentClick)
  },
  beforeDestroy () {
    document.removeEventListener('click', this.onDocumentClick)
  },
  methods: {
    isLeaf (item) {
      return !(item && item.children && item.children.length)
    },
    ensureTree () {
      this.loading = true
      return loadChinaArea().then(tree => {
        this.tree = tree || []
        this.applyValue(this.value)
      }).catch(() => {
        this.tree = []
      }).finally(() => { this.loading = false })
    },
    applyValue (value) {
      const path = findRegionPath(value, this.tree)
      this.provinceCode = path[0] && path[0].code
      this.cityCode = path[1] && path[1].code
      this.districtCode = path[2] && path[2].code
    },
    toggle () {
      if (this.disabled) return
      if (!this.open) this.ensureTree()
      this.open = !this.open
    },
    onDocumentClick (event) {
      if (!this.$el.contains(event.target)) this.open = false
    },
    emitSelection (node) {
      const code = node ? String(node.code) : undefined
      this.$emit('input', code)
      this.$emit('change', code)
    },
    pickProvince (item) {
      const already = this.provinceCode === item.code && !this.cityCode && !this.districtCode
      this.provinceCode = item.code
      this.cityCode = undefined
      this.districtCode = undefined
      this.emitSelection(item)
      // 任一级可选：末级直接关闭；非末级再点一次同一项确认关闭
      if (this.isLeaf(item) || already) this.open = false
    },
    pickCity (item) {
      const already = this.cityCode === item.code && !this.districtCode
      this.cityCode = item.code
      this.districtCode = undefined
      this.emitSelection(item)
      if (this.isLeaf(item) || already) this.open = false
    },
    pickDistrict (item) {
      this.districtCode = item.code
      this.emitSelection(item)
      this.open = false
    }
  }
}
</script>

<style scoped>
.dj-region-cascader { position: relative; width: 100%; }
.dj-region-trigger {
  width: 100%;
  height: 36px;
  border: 1px solid #d0d5dd;
  border-radius: 5px;
  background: #fff;
  padding: 0 34px 0 11px;
  display: flex;
  align-items: center;
  text-align: left;
  color: #1d2939;
  font: inherit;
  cursor: pointer;
  position: relative;
  transition: border-color .15s, box-shadow .15s;
}
.dj-region-trigger span {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.dj-region-trigger.placeholder { color: #98a2b3; }
.dj-region-trigger:hover,
.dj-region-cascader.open .dj-region-trigger { border-color: #4096ff; }
.dj-region-cascader.open .dj-region-trigger { box-shadow: 0 0 0 2px rgba(22, 119, 255, .10); }
.dj-region-trigger:disabled {
  background: #f5f5f5;
  color: rgba(0, 0, 0, .25);
  cursor: not-allowed;
}
.dj-region-trigger i {
  position: absolute;
  right: 12px;
  top: 50%;
  width: 7px;
  height: 7px;
  border-right: 1px solid #667085;
  border-bottom: 1px solid #667085;
  transform: translateY(-65%) rotate(45deg);
  transition: transform .15s;
}
.dj-region-cascader.open .dj-region-trigger i { transform: translateY(-25%) rotate(225deg); }
.dj-region-panel {
  position: absolute;
  left: 0;
  top: 41px;
  z-index: 99999;
  width: min(650px, calc(100vw - 120px));
  height: 260px;
  background: #fff;
  border: 1px solid #e4e7ec;
  border-radius: 7px;
  box-shadow: 0 10px 28px rgba(16, 24, 40, .14);
  overflow: hidden;
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
}
.dj-region-col { overflow: auto; padding: 6px 0; border-right: 1px solid #f0f1f3; background: #fff; }
.dj-region-col:last-child { border-right: 0; }
.dj-region-col.muted { background: #fafbfc; }
.dj-region-item {
  width: 100%;
  height: 36px;
  border: 0;
  background: transparent;
  padding: 0 11px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  color: #344054;
  font: inherit;
  cursor: pointer;
  text-align: left;
}
.dj-region-item:hover { background: #f5f8ff; color: #1677ff; }
.dj-region-item.active { background: #eaf3ff; color: #1677ff; font-weight: 500; }
.dj-region-item b { font-weight: 400; color: #98a2b3; }
.dj-region-item.active b { color: #1677ff; }
.dj-region-empty { padding: 14px 12px; color: #98a2b3; font-size: 12px; }
</style>
